import classNames from 'classnames';
import camelCase from 'lodash-es/camelCase';
import type { ReactNode } from 'react';

import { useCollectionUrlGenerator } from '../../hooks/useUrlGenerator';
import normalizeCardTheme from '../../lib/normalizeCardTheme';

import DocumentsList from '../lists/DocumentsList';
import CollectionSummary from '../summaries/CollectionSummary';

import styles from '../../styles/cards/collection-documents-card.module.css';

interface CollectionDocumentsCardProps extends Partial<Omit<CollectionSnippet, 'theme'>> {
    theme?: CardTheme | LegacyCardTheme | null;
    size?: CardSize;
    ratio?: CardRatio;
    listTitle?: ReactNode | null;
    listProps?: Record<string, unknown> | null;
    listQuery?: Record<string, unknown> | null;
    placeholder?: boolean;
    withoutText?: boolean;
    className?: string | null;
}

function CollectionDocumentsCard({
    id = null,
    type = null,
    slug = null,
    title = null,
    description = null,
    image = null,
    theme: providedTheme = null,
    size = null,
    ratio = null,
    listTitle = null,
    listProps = null,
    listQuery = null,
    placeholder = false,
    withoutText = false,
    className = null,
}: CollectionDocumentsCardProps) {
    const theme = normalizeCardTheme(providedTheme);
    const collectionUrl = useCollectionUrlGenerator();
    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.withoutText]: withoutText,
                },
                styles[camelCase(size as string)],
                styles[camelCase(ratio as string)],
                className,
            ])}
        >
            <CollectionSummary
                slug={slug}
                title={title}
                description={description}
                image={image}
                imagePosition="left"
                className={styles.summary}
                innerClassName={styles.summaryInner}
                imageClassName={styles.summaryImage}
                descriptionClassName={styles.summaryDescription}
                withLink
                link={
                    collectionUrl({
                        type,
                        slug,
                    }) || '#'
                }
            />
            {listTitle !== null ? <div className={styles.title}>{listTitle}</div> : null}
            <DocumentsList
                query={
                    !placeholder
                        ? {
                              collection: id,
                              ...listQuery,
                          }
                        : null
                }
                disabled={placeholder}
                cardType="horizontal"
                cardRatio={ratio}
                cardSize={size}
                cardTheme={theme}
                count={3}
                columns={1}
                placeholder={placeholder}
                className={styles.list}
                {...listProps}
            />
        </div>
    );
}

export default CollectionDocumentsCard;
